import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchOverview } from "./api.ts";
import { currentUserInitials } from "./auth.ts";
import { STATUS_LABEL, type AppCard, type Status } from "./types.ts";
import { KorrusMark } from "./components/Logo.tsx";
import { Sparkline } from "./components/Sparkline.tsx";
import { DetailPage } from "./DetailPage.tsx";
import { openApp, useRoute } from "./useRoute.ts";

const STATUS_ORDER: Record<Status, number> = { crit: 0, warn: 1, unknown: 2, ok: 3 };

/** Short names for the mosaic cells (env is shown separately as a badge). */
const SHORT: Record<string, string> = {
  "ESP RainMaker (prod)": "ESP RainMaker",
  "ecosenselighting.com": "ecosense",
  "NetSuite integrations": "netsuite",
  "mailbox-headers-sync": "mailbox-sync",
  "figma-shopify-harness": "figma-harness",
};
const shortName = (n: string) => SHORT[n] ?? n;

type SavedView = null | "prod" | "customer" | "notgreen";
const SAVED_VIEWS: Array<{ id: Exclude<SavedView, null>; label: string }> = [
  { id: "prod", label: "Prod only" },
  { id: "customer", label: "Customer-facing" },
  { id: "notgreen", label: "Anything not green" },
];
const CUSTOMER_FACING = new Set(["esp-rainmaker", "esp-rainmaker-prod", "korrus-com", "ecosense", "partlocator"]);
const matchesView = (a: AppCard, v: SavedView) =>
  v === null || (v === "prod" ? a.env === "prod" : v === "customer" ? CUSTOMER_FACING.has(a.id) : a.status !== "ok");

const hms = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour12: false });
const fmtUptime = (u?: number | null) => (u == null ? "—" : `${u.toFixed(1)}%`);
const fmtCost = (c?: number | null) => (c == null ? "—" : `$${c.toFixed(2)}`);
const relTime = (iso?: string | null) => {
  if (!iso) return "—";
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

function useClock(): number {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setT(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
}

export function App() {
  const route = useRoute();
  if (route.name === "app" && route.id) return <DetailPage id={route.id} />;
  return <Overview />;
}

type EnvFilter = "all" | "dev" | "prod";

function Overview() {
  const [env, setEnv] = useState<EnvFilter>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<SavedView>(null);
  const clock = useClock();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["overview"],
    queryFn: fetchOverview,
    refetchInterval: 60_000,
  });

  const all = data?.apps ?? [];
  const byWorst = (a: AppCard, b: AppCard) =>
    STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.displayName.localeCompare(b.displayName);

  const envApps = useMemo(
    () => all.filter((a) => env === "all" || a.env === env).sort(byWorst),
    [all, env],
  );
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return envApps.filter((a) => matchesView(a, view)).filter(
      (a) =>
        !q ||
        a.displayName.toLowerCase().includes(q) ||
        (a.owner ?? "").toLowerCase().includes(q) ||
        a.type.toLowerCase().includes(q),
    );
  }, [envApps, query, view]);

  const s = data?.summary;
  const needAttention = s ? s.crit + s.warn : 0;
  const worst: Status = s ? (s.crit ? "crit" : s.warn ? "warn" : "ok") : "ok";
  const alarmsFiring = all.reduce((n, a) => n + a.alarmCount, 0);
  const uptimeVals = all.map((a) => a.uptime24h).filter((u): u is number => u != null);
  const fleetUptime = uptimeVals.length ? uptimeVals.reduce((s, v) => s + v, 0) / uptimeVals.length : null;
  const upColor = (u: number) => (u >= 99.9 ? "var(--ok)" : u >= 99 ? "var(--amber)" : "var(--red)");
  const affected = envApps.filter((a) => a.status === "crit" || a.status === "warn");
  const prodAffected = affected.filter((a) => a.env === "prod");

  const verdictCopy = () => {
    if (needAttention === 0) return "All services operational. No alarms firing across the fleet.";
    const names = affected.slice(0, 4).map((a) => a.displayName).join(", ") + (affected.length > 4 ? ", …" : "");
    const prod = prodAffected.length
      ? ` ${prodAffected.length === 1 ? "One is" : `${prodAffected.length} are`} in production.`
      : " All in dev.";
    return `${names}.${prod}`;
  };

  return (
    <>
      <div className="topbar">
        <div className="brand"><KorrusMark /><span className="status-word">status</span></div>
        <div className="divider" />
        <div className="tabs" role="tablist" aria-label="Environment">
          {(["all", "dev", "prod"] as const).map((e) => (
            <button key={e} className={env === e ? "on" : ""} role="tab" aria-selected={env === e} onClick={() => setEnv(e)}>
              {e === "all" ? "All" : e.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="search" style={{ width: 240 }}>
          <span className="glyph">⌕</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter services, owners, types" aria-label="Filter services" />
        </div>
        <div className="spacer" />
        <span className="clock"><span className="live-dot" />{hms(clock)}</span>
        <div className="avatar">{currentUserInitials()}</div>
      </div>

      <main>
        {s && (
          <section className="verdict-row">
            <div className="verdict">
              <div className={`verdict-left sc-${worst}`}>
                <div className="eyebrow">Fleet verdict</div>
                <div className="verdict-headline">
                  <span className="n" style={{ color: "var(--sc)" }}>{needAttention}</span>
                  <span className="t">{needAttention ? "need attention" : "all operational"}</span>
                </div>
                <div className="verdict-body">{verdictCopy()}</div>
              </div>
              <div className="mosaic-wrap">
                <div className="eyebrow">{envApps.length} services</div>
                <div className="mosaic">
                  {envApps.map((a) => (
                    <button key={a.id} className={`cell cell-${a.status}`} onClick={() => openApp(a.id)}
                      title={`${a.displayName} (${a.env}) — ${STATUS_LABEL[a.status]}`}>
                      <span className="cn">{shortName(a.displayName)}</span>
                      <span className="cu">
                        <b className={`cell-env${a.env === "prod" ? " is-prod" : ""}`}>{a.env.toUpperCase()}</b>
                        {a.uptime24h != null ? ` · ${fmtUptime(a.uptime24h)}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="kpis">
              <div className="kpi"><div className="eyebrow">Fleet uptime 24h</div><div className="kv" style={{ color: fleetUptime == null ? "var(--fg-disabled)" : upColor(fleetUptime) }}>{fleetUptime == null ? "—" : `${fleetUptime.toFixed(2)}%`}</div><div className="kd">{fleetUptime == null ? "not yet wired" : `across ${uptimeVals.length} services`}</div></div>
              <div className="kpi"><div className="eyebrow">Alarms firing</div><div className="kv" style={{ color: alarmsFiring ? "var(--red)" : "var(--fg-1)" }}>{alarmsFiring}</div><div className="kd">{s.crit} critical · {s.warn} degraded</div></div>
              <div className="kpi"><div className="eyebrow">Spend today</div><div className="kv" style={{ color: data.spendToday == null ? "var(--fg-disabled)" : "var(--fg-1)" }}>{data.spendToday == null ? "—" : `$${Math.round(data.spendToday).toLocaleString()}`}</div><div className="kd">{data.spendToday == null ? "not yet wired" : "all accounts · yesterday"}</div></div>
            </div>
          </section>
        )}

        {data?.fleetRibbon && data.fleetRibbon.length > 0 && (
          <section className="ribbon-card">
            <div className="ribbon-head">
              <b>Fleet over 24 hours</b>
              <span>worst status across all services · 30-minute buckets</span>
            </div>
            <div className="ribbon">
              {data.fleetRibbon.map((b, i) => (
                <div key={i} className={`bar bar-${b.worst}`}
                  title={`${new Date(b.t).toLocaleString()} — ${b.worst === "crit" ? "critical" : "operational"}`} />
              ))}
            </div>
            <div className="ribbon-ticks">
              {[24, 18, 12, 6, 0].map((h, i) => (
                <span key={i}>{new Date(clock - h * 3600_000).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
              ))}
            </div>
          </section>
        )}

        {isLoading && <div className="state-msg">Loading fleet status…</div>}
        {isError && <div className="state-msg">Couldn’t load the status feed. Retrying…</div>}

        {data && (
          <div className="views">
            <span className="views-label">Saved views</span>
            {SAVED_VIEWS.map((v) => (
              <button key={v.id} className={`chip${view === v.id ? " on" : ""}`}
                onClick={() => setView(view === v.id ? null : v.id)}>{v.label}</button>
            ))}
          </div>
        )}

        {data && (
          <div className="svc-table">
            <div className="svc-grid svc-head">
              <span />
              <span style={{ paddingLeft: 14 }}>Service</span>
              <span>Env</span>
              <span>Type</span>
              <span>Uptime 24h</span>
              <span>Vitals</span>
              <span>Alarms</span>
              <span>Owner</span>
              <span className="r">$ / day</span>
              <span className="r">Last change</span>
            </div>
            {rows.map((a) => <ServiceRow key={a.id} app={a} />)}
            {rows.length === 0 ? (
              <div className="svc-foot">No services match “{query}” — clear the filter or try an owner name.</div>
            ) : (
              <div className="svc-foot">{rows.length} of {all.length} services shown — sorted worst-first, then alphabetically.</div>
            )}
          </div>
        )}
      </main>
    </>
  );
}

function uptimeCell(u?: number | null) {
  if (u == null) return <span className="svc-cell mut">—</span>;
  const col = u >= 99.9 ? "var(--ok)" : u >= 99 ? "var(--amber)" : "var(--red)";
  const w = Math.max(0, Math.min(100, ((u - 96) / 4) * 100));
  return (
    <span className="uptime">
      <span className="uv" style={{ color: col }}>{u.toFixed(1)}%</span>
      <span className="track"><span className="fill" style={{ width: `${w}%`, background: col }} /></span>
    </span>
  );
}

function ServiceRow({ app }: { app: AppCard }) {
  const open = () => openApp(app.id);
  return (
    <>
      {/* Desktop / tablet: full table row */}
      <div className={`svc-grid svc-row sc-${app.status}`} role="button" tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === "Enter") open(); }}>
        <span className="svc-statusbar" />
        <div className="svc-name"><span className="dot" /><span className="nm">{app.displayName}</span></div>
        <span><span className={`tag ${app.env === "prod" ? "tag-prod" : "tag-dev"}`}>{app.env}</span></span>
        <span className="svc-cell tag-type">{app.type}</span>
        <span>{uptimeCell(app.uptime24h)}</span>
        <div className="vitals">
          {app.vitals.slice(0, 3).map((v) => (
            <div className="vital" key={v.label}>
              <span className="vl">{v.label}</span>
              <span className="vv">{v.value}</span>
              <Sparkline series={v.series} />
            </div>
          ))}
        </div>
        <span className="alarms-cell" style={{ color: app.alarmCount > 0 ? "var(--red)" : "var(--fg-disabled)" }}>
          {app.alarmCount > 0 ? app.alarmCount : "—"}
        </span>
        <span className="svc-cell">{app.owner ?? "—"}</span>
        <span className="svc-cell r mut">{fmtCost(app.costPerDay)}</span>
        <span className="svc-cell r mut">{relTime(app.lastChangeAt)}</span>
      </div>

      {/* Phone: stacked card */}
      <button className={`svc-card sc-${app.status}`} onClick={open} aria-label={`${app.displayName} details`}>
        <span className="svc-statusbar" />
        <div className="sc-l1">
          <span className="dot" />
          <span className="nm">{app.displayName}</span>
          <span className={`tag ${app.env === "prod" ? "tag-prod" : "tag-dev"}`}>{app.env}</span>
        </div>
        <div className="sc-l2">
          <span className="sc-status">{STATUS_LABEL[app.status]}</span>
          {app.uptime24h != null && <><span className="sep">·</span><span>{app.uptime24h.toFixed(1)}% up</span></>}
        </div>
        <div className="sc-l3">
          <span style={{ color: app.alarmCount > 0 ? "var(--red)" : "var(--fg-3)" }}>
            {app.alarmCount > 0 ? `${app.alarmCount} alarm${app.alarmCount > 1 ? "s" : ""}` : "no alarms"}
          </span>
          <span className="sep">·</span>
          <span>{fmtCost(app.costPerDay)}{app.costPerDay != null ? "/day" : ""}</span>
          <span className="sc-arrow" aria-hidden>→</span>
        </div>
      </button>
    </>
  );
}
